import { Component, OnInit, inject, ChangeDetectionStrategy, signal, input, output } from '@angular/core';
import {FormsModule} from "@angular/forms";
import {MatProgressSpinner} from "@angular/material/progress-spinner";

import {Entity} from "../../../../entity/entity";
import {GuruEntity} from "../../../../model/guru-entity";
import {Specification} from "../../../../entity/specification";
import {SpecificationCharacteristic} from "../../../../entity/specification-characteristic";
import {GuruAllowable} from "../../../../model/guru-allowable";
import {ModelService} from "../../../../services/model-service";
import {SpecificationService} from "../../../../services/specification-service";
import {Router} from "@angular/router";
import {StringUtils} from '../../../../utils/string-utils';
import {firstValueFrom} from "rxjs";

@Component({
    selector: 'app-iec-riser-project-defaults',
    imports: [
    FormsModule,
    MatProgressSpinner
],
    templateUrl: './iec-riser-project-defaults.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    styleUrl: './iec-riser-project-defaults.component.css'
})
export class IecRiserProjectDefaultsComponent implements OnInit {
  private readonly modelService = inject(ModelService);
  private readonly specificationService = inject(SpecificationService);
  private readonly router = inject(Router);


  readonly entity = input.required<Entity>();
  readonly errorEvent = output<any>();
  private engineKey!: string;
  private invalidEntities!: string[];
  userSelections!: GuruEntity[];
  selectedValues!: GuruEntity[];
  isComplete!: boolean;
  isValid!: boolean;
  readonly isBusy = signal<boolean>(undefined!);

  ngOnInit(): void {
    if (StringUtils.isBlank(this.entity()?.id?.entityId)) {
      this.backToHome();
    }
    this.isBusy.set(true);
    this.initializeData().then(() => this.isBusy.set(false), (error: any) => this.handleError(error));
  }

  private async initializeData(): Promise<void> {
    this.engineKey = await firstValueFrom<string>(this.modelService.getEngineKey('ProjectDefaults'));
    const specifications: Specification[] = await firstValueFrom<Specification[]>(this.specificationService.getSpecifications(this.entity().id));
    if ((specifications?.length || 0) > 0) {
      const filtered: Specification[] = specifications.filter((spec: Specification) => spec.specificationType === 'PRO_USC');
      if (filtered.length === 1) {
        await firstValueFrom<string>(this.modelService.loadConfiguration(this.engineKey, filtered[0].specificationCharacteristics));
      }
    }
    const userSelections: GuruEntity[] = await firstValueFrom<GuruEntity[]>(this.modelService.getUserSelections(this.engineKey));
    this.userSelections = JSON.parse(JSON.stringify(userSelections));
    this.selectedValues = JSON.parse(JSON.stringify(userSelections));
  }

  private backToHome(): void {
    this.router.navigate(['/', 'main']).then(() => this.isBusy.set(false));
  }

  private resetStatus(): void {
    this.isBusy.set(true);
    this.errorEvent.emit(undefined);
    this.invalidEntities = null!;
    this.isComplete = false;
    this.isValid = false;
  }

  private handleError(error: any) {
    this.isBusy.set(false);
    this.errorEvent.emit(error);
  }

  getAllowableValues(entityName: string): GuruAllowable[] {
    const entity: GuruEntity | undefined = this.getEntityByName(entityName, this.userSelections);
    return entity?.Allowables || [];
  }

  private getEntityByName(entityName: string, pool: GuruEntity[]): GuruEntity | undefined {
    if ((pool?.length || 0) > 0) {
      const filtered: GuruEntity[] = pool.filter((entity: GuruEntity) => StringUtils.equalsIgnoreCase(entity.Name, entityName));
      if (filtered.length === 1) {
        return filtered[0];
      }
    }
    return undefined;
  }

  getSelectedValue(entityName: string): string {
    const entity: GuruEntity | undefined = this.getEntityByName(entityName, this.selectedValues);
    return entity?.Value || '';
  }

  validateSelections(entityName: string, newValue: string): void {
    this.resetStatus();
    this.updateSelectedValue(entityName, newValue).then(() => this.isBusy.set(false), (error: any) => this.handleError(error));
  }

  private async updateSelectedValue(entityName: string, newValue: string): Promise<void> {
    await firstValueFrom<string>(this.modelService.setValue(entityName, this.engineKey, newValue));
    if (StringUtils.equalsIgnoreCase(entityName, 'USRiserVendor')) {
      await firstValueFrom<GuruEntity[]>(this.modelService.executeHierarchy(this.engineKey, 'hierRiserVendorDefault'));
    }
    this.selectedValues = await firstValueFrom<GuruEntity[]>(this.modelService.getUserSelections(this.engineKey));
    this.invalidEntities = await firstValueFrom<string[]>(this.modelService.getInvalidEntities(this.engineKey));
    this.isComplete = await firstValueFrom<boolean>(this.modelService.isComplete(this.engineKey));
    this.isValid = await firstValueFrom<boolean>(this.modelService.isValid(this.engineKey));
    const characteristics: SpecificationCharacteristic[] = await firstValueFrom<SpecificationCharacteristic[]>(this.modelService.getCharacteristics(this.engineKey));
    if ((characteristics?.length || 0) > 0) {
      await firstValueFrom<string>(this.specificationService.saveProjectDefaults(this.entity().id, characteristics));
    }
  }

  isInvalid(entityName: string): boolean {
    return (this.invalidEntities?.length || 0) > 0 && this.invalidEntities.indexOf(entityName) >= 0;
  }

  isSelectionValid(entityName: string, value: string): boolean {
    const entity: GuruEntity | undefined = this.getEntityByName(entityName, this.selectedValues);
    const allowables: GuruAllowable[] = entity?.Allowables || [];
    const filtered: GuruAllowable[] = allowables.filter((allowable: GuruAllowable) => StringUtils.equalsIgnoreCase(allowable.Value, value));
    if (filtered && filtered.length == 1) {
      return filtered[0].IsValid;
    }
    return false;
  }

}
